import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Newspaper, ExternalLink, Loader2, AlertCircle, Search, RotateCcw, Filter } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import {
  Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, Alert, FormField,
} from '../../components/portal/ui'
import {
  listNews, NEWS_CATEGORY_KEYS, NEWS_CATEGORY_LABELS, type NewsItem, type NewsCategory,
} from '../../services/newsService'

// ================================================================================
// NEWS & UPDATES — curated, agenda-scoped TB programme news.
//
// News is admin-curated and tightly scoped to the TB-elimination agenda
// (NTEP, Ni-Kshay, PM TB Mukt Bharat Abhiyaan, Nikshay Poshan Yojana). Each
// item references a real official programme page (no fabricated statistics,
// named individuals or content). Pinned items lead.
// ================================================================================

const OFFICIAL_NEWS_URL = 'https://tbcindia.mohfw.gov.in/'
const size = 12

export default function NewsPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()

  const initialCategory = params.get('category') || 'all'
  const [category, setCategory] = useState(initialCategory)
  const [q, setQ] = useState('')

  const [items, setItems] = useState<NewsItem[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const categoryLabel = (key: string) =>
    t(`pages.news.categories.${key}`, { defaultValue: NEWS_CATEGORY_LABELS[key as NewsCategory] ?? key })

  // Debounce search.
  const [qDebounced, setQDebounced] = useState('')
  useEffect(() => { const td = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(td) }, [q])

  // Reflect the category filter in the URL (shareable deep links).
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (category === 'all') next.delete('category')
    else next.set('category', category)
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await listNews({ category: category === 'all' ? undefined : category, q: qDebounced || undefined, page, size })
      setItems(data.content); setTotal(data.totalElements); setTotalPages(data.totalPages)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.news.error', { defaultValue: 'Could not load news. Please try again.' }))
    } finally { setLoading(false) }
  }, [category, qDebounced, page, t])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [category, qDebounced])

  const activeFilters = [
    category !== 'all' && `${t('pages.news.filters.categoryLabel')}: ${categoryLabel(category)}`,
    qDebounced && `${t('common.search')}: ${qDebounced}`,
  ].filter(Boolean) as string[]

  const clearFilters = () => { setCategory('all'); setQ('') }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.news.seo.title', { defaultValue: 'News & Updates — TB programme news' })}
        description={t('pages.news.seo.description', { defaultValue: 'Curated, agenda-scoped TB programme news and updates — official Central TB Division / Ni-Kshay programme pages.' })}
        canonicalPath="/news"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.news', 'News'), path: '/news' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.news', 'News') }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.news.hero.eyebrow', { defaultValue: 'News & Updates' })}
            title={t('pages.news.hero.title', { defaultValue: 'TB Programme News & Updates' })}
            description={t('pages.news.hero.description', { defaultValue: 'A small, curated set of updates scoped to the TB-elimination agenda. Each item links to its official source.' })}
          />
        </Container>
      </Section>

      <Section className="bg-white pt-2">
        <Container>
          <Card className="p-5 mb-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField label={t('pages.news.filters.categoryLabel', { defaultValue: 'Category' })} htmlFor="news-cat">
                <select id="news-cat" value={category} onChange={(e) => setCategory(e.target.value)} className="select">
                  <option value="all">{t('pages.news.categories.all', { defaultValue: 'All categories' })}</option>
                  {NEWS_CATEGORY_KEYS.map((k) => <option key={k} value={k}>{categoryLabel(k)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.news.filters.searchLabel', { defaultValue: 'Search' })} htmlFor="news-search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-snow-400" aria-hidden="true" />
                  <input id="news-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('pages.news.filters.searchPlaceholder', { defaultValue: 'Search news…' })} className="input pl-9" />
                </div>
              </FormField>
            </div>
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-snow-100">
                <Filter className="w-4 h-4 text-snow-400" aria-hidden="true" />
                <span className="text-xs text-snow-500">{t('pages.news.filters.activeFilters', { defaultValue: 'Active filters:' })}</span>
                {activeFilters.map((f) => <Badge key={f} tone="slate">{f}</Badge>)}
                <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-[rgb(15_76_129)] hover:underline ml-1">
                  <RotateCcw className="w-3 h-3" aria-hidden="true" /> {t('pages.news.filters.clearAll', { defaultValue: 'Clear all' })}
                </button>
              </div>
            )}
          </Card>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-snow-500" aria-live="polite">
              {loading ? t('pages.news.loading', { defaultValue: 'Loading…' }) : t('pages.news.count', { count: total, defaultValue: `${total} updates` })}
            </p>
          </div>

          {error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-4" role="alert">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16 text-snow-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('pages.news.loadingUpdates', { defaultValue: 'Loading updates…' })}</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Newspaper className="w-7 h-7" />}
              title={t('pages.news.empty.title', { defaultValue: 'No updates yet' })}
              description={t('pages.news.empty.desc', { defaultValue: 'Curated, agenda-scoped updates will appear here. Meanwhile, visit the official Central TB Division portal for the latest.' })}
              action={
                <a href={OFFICIAL_NEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-sm inline-flex items-center gap-1">
                  <ExternalLink className="w-4 h-4" /> {t('pages.news.empty.browseOfficial', { defaultValue: 'Visit official portal' })}
                </a>
              }
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((n) => (
                <Card key={n.id} className="p-5 h-full flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge tone="blue">{categoryLabel(n.category)}</Badge>
                    {n.isPinned && <Badge tone="teal">{t('pages.news.pinned', { defaultValue: 'Pinned' })}</Badge>}
                  </div>
                  <h3 className="font-semibold text-snow-900 line-clamp-2">{n.title}</h3>
                  {n.summary && <p className="text-sm text-snow-600 line-clamp-3 mt-1 flex-1">{n.summary}</p>}
                  <div className="mt-3 pt-3 border-t border-snow-100 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-snow-500 truncate">
                      {n.publishedAt ? new Date(n.publishedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                      {n.source ? ` · ${n.source}` : ''}
                    </span>
                    {n.url && (
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-[rgb(15_76_129)] hover:underline shrink-0 min-h-[44px]">
                        {t('pages.news.readOfficial', { defaultValue: 'Read on official site' })} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline btn-sm disabled:opacity-50">{t('common.previous')}</button>
              <span className="text-xs text-snow-500">{t('common.pageXofY', { page: page + 1, total: totalPages })}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-outline btn-sm disabled:opacity-50">{t('common.next')}</button>
            </div>
          )}
        </Container>
      </Section>
    </PortalLayout>
  )
}